import React, { memo } from "react";
import { useTranslate } from "helpers";
import { IntegrationCardProps } from "./types";
import { IntegrationStatus } from "../../../../types";
import Badge from "ui/components/Badge/Badge";
import { Button } from "ui/components/Button/Button";

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  integration,
  onConfigure,
  onInstall,
  onUninstall,
}) => {
  const { t } = useTranslate();

  const getStatusBadge = () => {
    switch (integration.status) {
      case IntegrationStatus.CONFIGURED:
        return <Badge text={t("admin.integration.status.configured")} variant="success" height="small" />;
      case IntegrationStatus.INSTALLED:
        return <Badge text={t("admin.integration.status.installed")} variant="info" height="small" />;
      case IntegrationStatus.NOT_INSTALLED:
      default:
        return <Badge text={t("admin.integration.status.notinstalled")} variant="warning" height="small" />;
    }
  };

  const isInstalled = integration.status !== IntegrationStatus.NOT_INSTALLED;

  const handleAction = () => {
    if (isInstalled) onConfigure(integration.id);
    else onInstall(integration.id);
  };

  const handleUninstall = () => {
    if (onUninstall) onUninstall(integration.id);
  };

  return (
    <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
      {/* Header with group badge */}
      <div className="px-4 py-2">
        <span className="text-xs font-medium text-white uppercase tracking-wide">
          {t(`admin.integration.group.${integration.group}`)}
        </span>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Icon and Name */}
        <div className="flex items-center mb-4">
          <div className="w-16 h-16 flex items-center justify-center bg-slate-50 rounded-lg border border-slate-100">
            <img
              src={integration.icon}
              alt={integration.name}
              className="w-12 h-12 object-contain"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/media/integrations/integration-default.svg";
              }}
            />
          </div>
          <div className="ml-4 flex-1">
            <h3 className="text-lg font-semibold text-slate-800">{integration.name}</h3>
            {getStatusBadge()}
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-slate-600 mb-4 line-clamp-2">
          {t(integration.description)}
        </p>

        {/* Action Button */}
        <div className="flex justify-end gap-2">
          {isInstalled && onUninstall && (
            <Button variant="outlined" size="small" onClick={handleUninstall}>
              {t("admin.integration.uninstall")}
            </Button>
          )}
          <Button
            variant={isInstalled ? "secondary" : "primary"}
            size="small"
            onClick={handleAction}
          >
            {isInstalled ? t("admin.integration.configure") : t("admin.integration.install")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default memo(IntegrationCard);
